import React from 'react';
import { Helmet } from 'react-helmet';
import { useI18n } from '../i18n/I18nContext';
import { getLocaleDefinition } from '../i18n/locales';
import { getLanguageAlternates, getLocalizedPath } from '../i18n/localizedRoutes';

const SITE_NAME = 'YCH-YOYO';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://ych-yoyo.com').replace(/\/$/, '');
const DEFAULT_IMAGE = '/images/ych_yoyo_logo.jpg';
const serializeSchema = (schema) => JSON.stringify(schema)
  .replace(/</g, '\\u003c')
  .replace(/\u2028/g, '\\u2028')
  .replace(/\u2029/g, '\\u2029');

export const getSiteUrl = () => {
  const envUrl = process.env.REACT_APP_SITE_URL;
  if (envUrl) {
    return envUrl.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return SITE_URL;
};

const toAbsoluteUrl = (url, siteUrl) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${siteUrl}${url.startsWith('/') ? url : `/${url}`}`;
};

const normalizePath = (path) => {
  if (!path) return '/';
  const base = String(path).split('?')[0].split('#')[0] || '/';
  return base.startsWith('/') ? base : `/${base}`;
};

const Seo = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  structuredData = [],
}) => {
  const { locale, t } = useI18n();
  const resolvedDescription = description || t('seo.defaultDescription');
  const { ogLocale } = getLocaleDefinition(locale);
  const siteUrl = getSiteUrl();
  const canonicalPath = getLocalizedPath(normalizePath(path), locale);
  const languageAlternates = getLanguageAlternates(canonicalPath);
  const canonicalUrl = siteUrl ? `${siteUrl}${canonicalPath}` : canonicalPath;
  const imageUrl = toAbsoluteUrl(image, siteUrl);
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';
  const schemas = Array.isArray(structuredData)
    ? structuredData.filter(Boolean)
    : structuredData
      ? [structuredData]
      : [];

  const callerProvidedOrganization = schemas.some(
    (schema) => schema && schema['@type'] === 'Organization'
  );

  const organizationSchema = callerProvidedOrganization
    ? null
    : {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: siteUrl,
        logo: toAbsoluteUrl(DEFAULT_IMAGE, siteUrl),
      };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <html lang={locale} />
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={ogLocale} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      <link rel="alternate" hrefLang="x-default" href={languageAlternates.length ? `${siteUrl}${languageAlternates[0].path}` : canonicalUrl} />
      {languageAlternates.length ? languageAlternates.map((alternate) => (
        <link key={alternate.locale} rel="alternate" hrefLang={alternate.locale} href={`${siteUrl}${alternate.path}`} />
      )) : <link rel="alternate" hrefLang="en" href={canonicalUrl} />}

      {organizationSchema && (
        <script type="application/ld+json">
          {serializeSchema(organizationSchema)}
        </script>
      )}

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {serializeSchema(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
